import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { teamUserService, type Permission, type CreateRoleRequest } from '@/services/teamUserService';
import { toast } from 'react-hot-toast';
import { PageHeader } from '@/components/ui';

interface PermissionRow {
  permission: Permission;
  sandbox: boolean;
  production: boolean;
}

interface CreateRolePageProps {
  onBack?: () => void;
}

export const CreateRolePage: React.FC<CreateRolePageProps> = ({ onBack }) => {
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectAllSandbox, setSelectAllSandbox] = useState(false);
  const [selectAllProduction, setSelectAllProduction] = useState(false);
  const [selectAllChannels, setSelectAllChannels] = useState(false);

  useEffect(() => {
    loadPermissions();
  }, []);

  // Update select all state when permissions change
  useEffect(() => {
    if (permissions.length > 0) {
      const allSandboxSelected = permissions.every((p) => p.sandbox);
      const allProductionSelected = permissions.every((p) => p.production);
      const allChannelsSelected = permissions.every((p) => p.sandbox && p.production);

      setSelectAllSandbox(allSandboxSelected);
      setSelectAllProduction(allProductionSelected);
      setSelectAllChannels(allChannelsSelected);
    }
  }, [permissions]);

  const loadPermissions = async () => {
    setIsLoading(true);
    try {
      const data = await teamUserService.getPermissionList();
      setPermissions(
        data.map((perm) => ({
          permission: perm,
          sandbox: false,
          production: false,
        }))
      );
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast.error('Failed to load permissions', {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermissionToggle = (permissionUuid: string, environment: 'sandbox' | 'production') => {
    setPermissions((prev) =>
      prev.map((perm) =>
        perm.permission.uuid === permissionUuid
          ? { ...perm, [environment]: !perm[environment] }
          : perm
      )
    );
  };

  const handleSelectAll = (environment: 'sandbox' | 'production') => {
    const newValue = environment === 'sandbox' ? !selectAllSandbox : !selectAllProduction;

    if (environment === 'sandbox') {
      setSelectAllSandbox(newValue);
    } else {
      setSelectAllProduction(newValue);
    }

    setPermissions((prev) =>
      prev.map((perm) => ({
        ...perm,
        [environment]: newValue,
      }))
    );
  };

  const handleSelectAllChannels = () => {
    const newValue = !selectAllChannels;
    setSelectAllChannels(newValue);

    setPermissions((prev) =>
      prev.map((perm) => ({
        ...perm,
        sandbox: newValue,
        production: newValue,
      }))
    );

    setSelectAllSandbox(newValue);
    setSelectAllProduction(newValue);
  };

  const handleSubmit = async () => {
    if (!roleName.trim()) {
      toast.error('Role name is required', {
        style: { background: '#d32f2f', color: '#fff' },
      });
      return;
    }

    if (roleName.length > 100) {
      toast.error('Role name must not exceed 100 characters', {
        style: { background: '#d32f2f', color: '#fff' },
      });
      return;
    }

    // Collect all selected permissions (both sandbox and production)
    const selectedPermissions: string[] = [];
    permissions.forEach((permRow) => {
      if (permRow.sandbox || permRow.production) {
        selectedPermissions.push(permRow.permission.uuid);
      }
    });

    if (selectedPermissions.length === 0) {
      toast.error('Please select at least one permission', {
        style: { background: '#d32f2f', color: '#fff' },
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const request: CreateRoleRequest = {
        permission_list: selectedPermissions,
        role_name: roleName,
        role_description: roleDescription || undefined,
      };

      await teamUserService.createRole(request);
      toast.success('Role created successfully', {
        style: { background: '#43a047', color: '#fff' },
      });
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error('Failed to create role', {
        style: { background: '#d32f2f', color: '#fff' },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group permissions by section
  const groupedPermissions = permissions.reduce((acc, permRow) => {
    const section = permRow.permission.section;
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(permRow);
    return acc;
  }, {} as Record<string, PermissionRow[]>);

  const headerActions = [
    {
      label: 'Cancel',
      onClick: () => {
        if (onBack) {
          onBack();
        }
      },
      variant: 'outlined' as const,
    },
    {
      label: isSubmitting ? 'Creating...' : 'Create',
      onClick: handleSubmit,
      variant: 'contained' as const,
      disabled: isSubmitting,
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Create role"
        //actions={headerActions}
        showDivider={false}
      />

      {/* <Box sx={{ mb: 4, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mr: 1 }}>
            Custom roles
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Customize access to features and functionalities for your team using custom roles.
          </Typography>
        </Box>
      </Box> */}

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Role
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter a name"
              label="Role name"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              error={roleName.length > 100}
              helperText={roleName.length > 100 ? 'Role name must not exceed 100 characters' : ''}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              placeholder="Enter a description"
              label="Role description"
              value={roleDescription}
              onChange={(e) => setRoleDescription(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </Box>
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mr: 1 }}>
            Set permissions
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Section</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Permission</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Select All
                    </Typography>
                    <Checkbox
                      checked={selectAllChannels}
                      onChange={handleSelectAllChannels}
                      size="small"
                    />

                  </TableCell>
                </TableRow>

              </TableHead>
              <TableBody>
                {Object.entries(groupedPermissions).map(([section, sectionPermissions]) =>
                  sectionPermissions.map((permRow) => (
                    <TableRow key={permRow.permission.uuid}>
                      <TableCell>{section}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {permRow.permission.permission_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {permRow.permission.description}
                          </Typography>
                          {permRow.permission.permission_name.includes('ACCOUNTS') ||
                            permRow.permission.permission_name.includes('TEAM') ||
                            permRow.permission.permission_name.includes('KEYS') ? (
                            <Tooltip title="Warning: This permission grants sensitive access">
                              <WarningIcon
                                sx={{ fontSize: 16, color: 'warning.main', ml: 0.5 }}
                              />
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={permRow.production}
                          onChange={() => handlePermissionToggle(permRow.permission.uuid, 'production')}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

        )}
      </Box>
      <Box>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={onBack}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Creating...' : 'Submit'}
          </Button>
        </DialogActions>
      </Box>
    </Box>
  );
};

export default CreateRolePage;

